import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier si l'utilisateur est déjà connecté (via le localStorage)
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        const token = localStorage.getItem('authToken');
        
        if (token) {
          // Configuration pour inclure le token dans les headers
          axios.defaults.headers.common['x-auth-token'] = token;
          
          // Vérifier la validité du token auprès du serveur
          const res = await axios.get('/api/auth/user');
          setCurrentUser(res.data);
        }
      } catch (err) {
        // Si le token n'est pas valide, le supprimer
        localStorage.removeItem('authToken');
        delete axios.defaults.headers.common['x-auth-token'];
        console.error("Erreur d'authentification", err);
        setError(err.response?.data?.msg || "Erreur d'authentification");
      } finally {
        setLoading(false);
      }
    };
    
    checkLoggedIn();
  }, []);

  // Fonction de connexion
  const login = async (email, password) => {
    try {
      setLoading(true);
      const res = await axios.post('/api/auth/login', { email, password });
      
      // Stocker le token dans le localStorage
      localStorage.setItem('authToken', res.data.token);
      
      // Configurer axios pour inclure le token dans tous les futurs appels
      axios.defaults.headers.common['x-auth-token'] = res.data.token;
      
      // Obtenir les informations de l'utilisateur
      const userRes = await axios.get('/api/auth/user');
      setCurrentUser(userRes.data);
      setError(null);
      return true;
    } catch (err) {
      setError(err.response?.data?.msg || "Erreur de connexion");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    // Supprimer le token du localStorage
    localStorage.removeItem('authToken');
    
    // Supprimer le token des headers par défaut
    delete axios.defaults.headers.common['x-auth-token'];
    
    // Réinitialiser l'état
    setCurrentUser(null);
  };

  // Vérifier si l'utilisateur est administrateur
  const isAdmin = () => {
    return currentUser?.role === 'admin';
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;